import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope } from './_shared';

// ============================================
// Domain Operations — multi-CDN binding provisioning (pre-experimental)
// GET  /cdn/domains/:id/operations/bindings   → current bindings + candidates
// POST /cdn/domains/:id/operations/provision  → 202 { group_id, status_url }
// Poll GET /cdn/jobs/:group_id for per-binding progress.
//
// The backend 404s orgs outside the env allowlist, so useOperationsView returns
// null for them and the UI hides the whole card — no separate feature flag.
// ============================================

export interface OperationsBinding {
  binding_id: string;
  provider: string;
  status: string;
  uid?: string;
  is_core: boolean;
  drifted: boolean;
}

export interface CDNCapabilities {
  supported: string[];
  unsupported: string[];
}

export interface OperationsCandidate {
  provider: string;
  service_id: string;
  capabilities: CDNCapabilities;
}

export interface OperationsView {
  domain_id: string;
  domain_name: string;
  bindings: OperationsBinding[];
  candidates: OperationsCandidate[];
}

export interface ProvisionAcceptedBinding {
  job_id: string;
  provider: string;
  binding_id: string;
  status: string;
}

export interface ProvisionAccepted {
  group_id: string;
  operation: string;
  status_url: string;
  bindings: ProvisionAcceptedBinding[];
  already_bound?: string[];
}

const operationsKey = (domainId: string) => ['cdn', 'domains', domainId, 'operations'] as const;
const jobGroupKey = (groupId: string) => ['cdn', 'jobs', groupId] as const;

/**
 * Current bindings + provision candidates for the domain. Returns null when the
 * caller's org is outside the experimental allowlist (backend 404) — the UI
 * treats null as "feature unavailable" and hides the card.
 */
export function useOperationsView(domainId: string | undefined) {
  return useQuery({
    queryKey: operationsKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<OperationsView | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<OperationsView>>(
          `/cdn/domains/${domainId}/operations/bindings`,
        );
        return res.data?.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 403) return null; // feature off for this org
        throw e;
      }
    },
  });
}

/** Provision the not-yet-bound family secondaries + sync config. Returns the 202 payload. */
export function useProvisionBindings(
  domainId: string,
  options?: UseMutationOptions<ProvisionAccepted, unknown, string | undefined>,
) {
  const qc = useQueryClient();
  return useMutation({
    // serviceId targets a single provider (add one at a time); omit for all.
    mutationFn: async (serviceId?: string): Promise<ProvisionAccepted> => {
      const res = await serviceApiV3.post<Envelope<ProvisionAccepted>>(
        `/cdn/domains/${domainId}/operations/provision`,
        serviceId ? { service_id: serviceId } : {},
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: operationsKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

export interface BindingActionResult {
  binding_id: string;
  provider: string;
  status: string;
  vendor_applied: boolean;
  note?: string;
}

type BindingAction = 'disable' | 'enable' | 'delete';

/**
 * Disable / enable / soft-delete a secondary binding. disable/enable POST to the
 * action sub-path; delete is a DELETE on the binding. All invalidate the view.
 */
export function useBindingAction(
  domainId: string,
  action: BindingAction,
  options?: UseMutationOptions<BindingActionResult, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bindingId: string): Promise<BindingActionResult> => {
      const base = `/cdn/domains/${domainId}/operations/bindings/${bindingId}`;
      const res =
        action === 'delete'
          ? await serviceApiV3.delete<Envelope<BindingActionResult>>(base)
          : await serviceApiV3.post<Envelope<BindingActionResult>>(`${base}/${action}`, {});
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: operationsKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Live vendor status — GET /cdn/domains/:id/operations/healthz
// Fan-outs a live GetDomain across the domain's bindings and returns each
// binding's REAL operational status from the provider (on-air / active),
// distinct from the local registry status. Hits live vendor APIs (seconds), so
// it is fetched on demand (enabled toggle), never auto-polled.
// ============================================

export interface BindingLiveStatus {
  binding_id: string;
  provider: string;
  uid?: string;
  is_core: boolean;
  local_status: string;
  live_status?: string; // canonical: active | provisioning | suspended | unknown
  raw_status?: string; // vendor's own wording (e.g. EdgeNext "serving" / "deploying")
  on_air: boolean;
  cname?: string;
  error?: string;
}

const liveStatusKey = (domainId: string) => ['cdn', 'domains', domainId, 'operations', 'live-status'] as const;

// LIVE_STATUS_POLL_MS is how often the live status refreshes while any binding is
// still provisioning (e.g. Hawk "deploying") — fast enough to watch a deploy
// settle without hammering the live vendor APIs.
const LIVE_STATUS_POLL_MS = 10_000;

/**
 * Live vendor status for every binding of the domain. Fetches automatically on
 * mount and keeps polling while any binding is still provisioning (transitional
 * vendor state), then settles — no manual refresh needed. Returns null for orgs
 * outside the allowlist (backend 404/403).
 */
export function useOperationsLiveStatus(domainId: string | undefined) {
  return useQuery({
    queryKey: liveStatusKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    staleTime: 0,
    // Poll while a binding is mid-deploy; stop once every binding has settled
    // (active/suspended/error) so we don't call the vendors forever.
    refetchInterval: (query) => {
      if (query.state.error) return false;
      const rows = query.state.data as BindingLiveStatus[] | null | undefined;
      if (!rows) return false; // feature off for this org
      const anyProvisioning = rows.some((r) => !r.error && r.live_status === 'provisioning');
      return anyProvisioning ? LIVE_STATUS_POLL_MS : false;
    },
    queryFn: async (): Promise<BindingLiveStatus[] | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<{ bindings: BindingLiveStatus[] }>>(
          `/cdn/domains/${domainId}/operations/healthz`,
        );
        return res.data?.data?.bindings ?? [];
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 403) return null;
        throw e;
      }
    },
  });
}

export interface JobGroupBinding {
  job_id: string;
  provider_name: string;
  status: string;
  vendor_uid?: string;
  error?: string;
}

export interface JobGroupDetail {
  group_id: string;
  operation: string;
  aggregated: string; // ok | partial | failed | pending | running
  total: number;
  done: number;
  failed: number;
  active: number;
  bindings: JobGroupBinding[];
}

const JOB_ACTIVE = new Set(['queued', 'pending', 'running', 'dispatching']);

/**
 * Poll a job group while any binding is still active. Pass groupId=null to
 * disable. On completion the caller should refetch the operations view.
 */
export function useJobGroup(groupId: string | null) {
  return useQuery({
    queryKey: jobGroupKey(groupId ?? ''),
    enabled: !!groupId,
    retry: false,
    refetchInterval: (query) => {
      // Stop polling on error (e.g. an empty group that 404s) or once terminal.
      if (query.state.error) return false;
      const d = query.state.data as JobGroupDetail | undefined;
      if (!d) return 2000;
      const stillActive = d.active > 0 || d.bindings.some((b) => JOB_ACTIVE.has(b.status));
      return stillActive ? 2000 : false;
    },
    queryFn: async (): Promise<JobGroupDetail | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<JobGroupDetail>>(`/cdn/jobs/${groupId}`);
        return res.data.data;
      } catch (e) {
        // An empty/expired group returns 404 — treat as "nothing to poll" rather
        // than surfacing a raw error to the card.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw e;
      }
    },
  });
}
